import { useEffect, useState } from "react";

const TICKER_ITEMS = [
    { key: "nifty", label: "Nifty 50" },
    { key: "sensex", label: "Sensex" },
    { key: "usdInr", label: "USD/INR" },
    { key: "gold", label: "Gold /g" },
    { key: "silver", label: "Silver /g" },
];

const isValidQuote = (quote) =>
    Boolean(quote) &&
    Number.isFinite(quote.value) &&
    Number.isFinite(quote.change) &&
    Number.isFinite(quote.changePercent);

const isValidMarketData = (json) =>
    Boolean(json) &&
    typeof json.lastUpdated === "string" &&
    !Number.isNaN(new Date(json.lastUpdated).getTime()) &&
    TICKER_ITEMS.every((item) => isValidQuote(json[item.key]));

const formatRelativeTime = (isoString) => {
    const minutes = Math.max(0, Math.round((Date.now() - new Date(isoString).getTime()) / 60000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.round(minutes / 60)}h ago`;
};

const MarketTicker = ({ variant = "default" }) => {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [, forceRefresh] = useState(0);

    useEffect(() => {
        let cancelled = false;

        fetch("/data/marketData.json", { cache: "no-store" })
            .then((res) => {
                if (!res.ok) throw new Error("Market data unavailable");
                return res.json();
            })
            .then((json) => {
                if (cancelled) return;
                if (!isValidMarketData(json)) throw new Error("Malformed market data");
                setData(json);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!data) return undefined;
        const interval = setInterval(() => forceRefresh((n) => n + 1), 60_000);
        return () => clearInterval(interval);
    }, [data]);

    if (failed || !data) return null;

    const visibleItems = variant === "header" ? TICKER_ITEMS.slice(0, 3) : TICKER_ITEMS;

    return (
        <div
            className={`market-ticker market-ticker-${variant}`}
            role="group"
            aria-label="Market snapshot"
        >
            <div className="market-ticker-track">
                {visibleItems.map((item) => {
                    const quote = data[item.key];
                    const isUp = quote.change >= 0;
                    return (
                        <div className="market-ticker-item" key={item.key}>
                            <span className="market-ticker-label">{item.label}</span>
                            <span className="market-ticker-value">
                                {quote.value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </span>
                            <span className={`market-ticker-change ${isUp ? "is-up" : "is-down"}`}>
                                {isUp ? "▲" : "▼"}{" "}
                                {Math.abs(quote.change).toLocaleString("en-IN", { maximumFractionDigits: 2 })} (
                                {Math.abs(quote.changePercent).toFixed(2)}%)
                            </span>
                        </div>
                    );
                })}
            </div>
            {variant !== "header" && <span className="market-ticker-updated">Updated {formatRelativeTime(data.lastUpdated)}</span>}
        </div>
    );
};

export default MarketTicker;
